// pages/admin/AdminRewards.tsx

import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Avatar,
  InputAdornment,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Refresh,
  Search,
  Person,
  Stars,
  Email,
  CalendarToday,
  EmojiEvents,
  Visibility,
  Close,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListAdminRewardsQuery,
  UserReward,
} from '../../features/admin/adminRewardsApi.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
  blue: '#2c6ecb',
  purple: '#5c6ac4',
  gold: '#ffd700',
};

const AdminRewards = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Detail dialog state
  const [selectedReward, setSelectedReward] = useState<UserReward | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // ✅ Scroll state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);

  // API hooks
  const {
    data,
    isLoading,
    error,
    isError,
    refetch
  } = useListAdminRewardsQuery();

  const userRewards = data?.rewards || [];
  const totalRecords = data?.total || 0;

  // ✅ Scroll detection
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const checkScroll = () => {
      setShowScrollUp(el.scrollTop > 100);
      setShowScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 100);
    };

    checkScroll();
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  // Re-check when data loads
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setTimeout(() => {
      setShowScrollUp(el.scrollTop > 100);
      setShowScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 100);
    }, 200);
  }, [userRewards, isLoading]);

  // ✅ Scroll functions
  const scrollUp = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollDown = () => {
    const el = scrollContainerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleRefresh = () => refetch();

  const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetail = (reward: UserReward) => {
    setSelectedReward(reward);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedReward(null);
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403: return 'You don\'t have permission to view user rewards.';
        case 401: return 'Please sign in to continue.';
        case 404: return 'No user rewards found.';
        case 500: return 'Something went wrong. Please try again.';
        default: return 'Unable to load user rewards. Please try again.';
      }
    }
    return 'Network error. Please check your connection.';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const filteredRewards = useMemo(() => {
    return userRewards.filter((reward: UserReward) =>
      !searchStr ||
      reward.name?.toLowerCase().includes(searchStr.toLowerCase()) ||
      reward.user_email?.toLowerCase().includes(searchStr.toLowerCase()) ||
      reward.points?.toLowerCase().includes(searchStr.toLowerCase()) ||
      reward.user_id?.toString().includes(searchStr)
    );
  }, [userRewards, searchStr]);

  const paginatedRewards = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRewards.slice(start, start + rowsPerPage);
  }, [filteredRewards, page, rowsPerPage]);

  const getPointsColor = (points: string) => {
    const val = parseFloat(points);
    if (val >= 50) return shopifyColors.success;
    if (val >= 20) return shopifyColors.warning;
    return shopifyColors.textSecondary;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: shopifyColors.background }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        {/* ✅ position: relative needed so scroll buttons position correctly inside */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />

          {/* Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: shopifyColors.textPrimary, mb: 1 }}>
                  User Rewards
                </Typography>
                <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                  View all user reward points and activity
                </Typography>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2,
                  backgroundColor: `${shopifyColors.primary}10`,
                  borderRadius: '8px',
                  border: `1px solid ${shopifyColors.primary}30`,
                }}
              >
                <EmojiEvents sx={{ fontSize: 32, color: shopifyColors.gold }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: shopifyColors.textPrimary }}>
                    {totalRecords}
                  </Typography>
                  <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                    Total Users
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* ✅ Scrollable content area — ref attached here */}
          <Box
            ref={scrollContainerRef}
            sx={{
              flexGrow: 1,
              p: 3,
              pt: 2,
              overflow: 'auto',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
              '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb:hover': { background: '#a8a8a8' },
            }}
          >
            {/* Filter Card */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Search by name, email, or points..."
                  value={searchStr}
                  onChange={(e) => setSearchStr(e.target.value)}
                  sx={{
                    minWidth: 300, flexGrow: 1,
                    '& .MuiOutlinedInput-root': { borderRadius: '6px' }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={isLoading}
                  size="small"
                  sx={{
                    borderRadius: '6px', textTransform: 'none',
                    borderColor: shopifyColors.border,
                    color: shopifyColors.textPrimary,
                  }}
                >
                  Refresh
                </Button>
              </Box>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                {filteredRewards.length} user{filteredRewards.length === 1 ? '' : 's'}
                {searchStr && ` matching "${searchStr}"`}
              </Typography>
            )}

            {/* Loading */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>Loading user rewards...</Typography>
              </Box>
            )}

            {/* Error */}
            {isError && !isLoading && (
              <Alert
                severity="error"
                sx={{ mb: 2, borderRadius: '6px', border: `1px solid ${shopifyColors.error}20` }}
                action={<Button color="inherit" size="small" onClick={handleRefresh}>Try again</Button>}
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty */}
            {!isLoading && !isError && filteredRewards.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <EmojiEvents sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  {searchStr ? 'No users found' : 'No user rewards yet'}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                  {searchStr
                    ? `No users match your search "${searchStr}"`
                    : 'User reward data will appear here once users earn points'}
                </Typography>
              </Card>
            )}

            {/* Table */}
            {!isLoading && filteredRewards.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {['#', 'User', 'Email', 'User ID', 'Points', 'Last Updated'].map((h) => (
                          <TableCell key={h} sx={{ bgcolor: shopifyColors.background, fontWeight: 600, color: shopifyColors.textPrimary }}>
                            {h}
                          </TableCell>
                        ))}
                        <TableCell sx={{ bgcolor: shopifyColors.background, fontWeight: 600, color: shopifyColors.textPrimary, textAlign: 'right' }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRewards.map((reward: UserReward, index: number) => (
                        <TableRow
                          key={reward.id}
                          sx={{ '&:hover': { backgroundColor: `${shopifyColors.background}80` } }}
                        >
                          <TableCell sx={{ color: shopifyColors.textSecondary }}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: shopifyColors.blue, width: 32, height: 32 }}>
                                <Person sx={{ fontSize: 18 }} />
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: shopifyColors.textPrimary }}>
                                {reward.name || 'Unknown User'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Email sx={{ fontSize: 16, color: shopifyColors.textSecondary }} />
                              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                {reward.user_email || 'No email'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                              #{reward.user_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<Stars sx={{ fontSize: 14 }} />}
                              label={`${reward.points} Points`}
                              size="small"
                              sx={{
                                borderRadius: '6px', fontWeight: 600, fontSize: '0.75rem',
                                backgroundColor: `${getPointsColor(reward.points)}20`,
                                color: getPointsColor(reward.points),
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarToday sx={{ fontSize: 16, color: shopifyColors.textSecondary }} />
                              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                {formatDate(reward.updated_at)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetail(reward)}
                                sx={{
                                  color: shopifyColors.blue,
                                  '&:hover': { backgroundColor: `${shopifyColors.blue}15` }
                                }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ borderTop: `1px solid ${shopifyColors.border}` }}>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={filteredRewards.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </Box>
              </Card>
            )}
          </Box>

          {/* ✅ Scroll Arrow Buttons — positioned inside Main Content, bottom-right */}
          {(showScrollUp || showScrollDown) && (
            <Box
              sx={{
                position: 'absolute',
                right: 16,
                bottom: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                zIndex: 100,
              }}
            >
              {showScrollUp && (
                <Tooltip title="Scroll to top" placement="left">
                  <IconButton
                    onClick={scrollUp}
                    size="small"
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: shopifyColors.surface,
                      border: `1px solid ${shopifyColors.border}`,
                      borderRadius: '6px',
                      color: shopifyColors.textSecondary,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: shopifyColors.primary,
                        color: '#fff',
                        borderColor: shopifyColors.primary,
                        boxShadow: '0 4px 12px rgba(0,128,96,0.25)',
                      },
                    }}
                  >
                    <KeyboardArrowUp fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {showScrollDown && (
                <Tooltip title="Scroll to bottom" placement="left">
                  <IconButton
                    onClick={scrollDown}
                    size="small"
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: shopifyColors.surface,
                      border: `1px solid ${shopifyColors.border}`,
                      borderRadius: '6px',
                      color: shopifyColors.textSecondary,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: shopifyColors.primary,
                        color: '#fff',
                        borderColor: shopifyColors.primary,
                        boxShadow: '0 4px 12px rgba(0,128,96,0.25)',
                      },
                    }}
                  >
                    <KeyboardArrowDown fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

        </Box>
      </Box>

      {/* User Reward Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={handleCloseDetail}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${shopifyColors.border}`,
          backgroundColor: shopifyColors.background,
          pb: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmojiEvents sx={{ color: shopifyColors.gold }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                Reward Details
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDetail} size="small" sx={{ color: shopifyColors.textSecondary }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedReward && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Avatar + name */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2, p: 2,
                bgcolor: shopifyColors.background,
                borderRadius: '8px',
                border: `1px solid ${shopifyColors.border}`,
              }}>
                <Avatar sx={{ bgcolor: shopifyColors.blue, width: 52, height: 52 }}>
                  <Person sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                    {selectedReward.name || 'Unknown User'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                    User ID: #{selectedReward.user_id}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              {/* Detail rows */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  {
                    label: 'Email',
                    value: selectedReward.user_email || 'No email',
                    icon: <Email fontSize="small" sx={{ color: shopifyColors.textSecondary }} />,
                  },
                  {
                    label: 'User ID',
                    value: `#${selectedReward.user_id}`,
                    icon: <Person fontSize="small" sx={{ color: shopifyColors.textSecondary }} />,
                  },
                  {
                    label: 'Last Updated',
                    value: formatDate(selectedReward.updated_at),
                    icon: <CalendarToday fontSize="small" sx={{ color: shopifyColors.textSecondary }} />,
                  },
                ].map((row) => (
                  <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {row.icon}
                      <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>{row.label}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: shopifyColors.textPrimary }}>
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider />

              {/* Points highlight */}
              <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                p: 3,
                bgcolor: `${getPointsColor(selectedReward.points)}10`,
                borderRadius: '8px',
                border: `1px solid ${getPointsColor(selectedReward.points)}30`,
              }}>
                <Stars sx={{ fontSize: 36, color: getPointsColor(selectedReward.points) }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: getPointsColor(selectedReward.points) }}>
                  {selectedReward.points}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, fontWeight: 500 }}>
                  Total Reward Points
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: `1px solid ${shopifyColors.border}` }}>
          <Button
            onClick={handleCloseDetail}
            variant="outlined"
            sx={{
              borderRadius: '6px', textTransform: 'none', fontWeight: 600,
              borderColor: shopifyColors.border,
              color: '#eeeeee', bgcolor: '#f44336'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminRewards;